// src/aiService.js

const GROQ_API_KEY = "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";

export const generateTriageSummary = async (role, aggregatedResult, chunks, historyContext) => {
    if (role === 'doctor') {
        const prompt = `You are an Agentic ER triage AI. The patient just recorded a session with these 10-second segments: [${chunks.join(", ")}]. The aggregated result is ${aggregatedResult}. For context, their recent past history is: [${historyContext}]. 
        
        You MUST evaluate the situation and return EXACTLY a JSON object. Do not include markdown, code blocks, or conversational text.
        
        Use these clinical rules to determine the IoT actuators:
        1. Stable (Current is Normal/Sighing, History is mostly Normal): risk_level="LOW", alarm=false, lights=false, gsm=false.
        2. Chronic Degradation (Current is Fast Tachypnea/Slow Bradypnea, History is Normal/Sighing): risk_level="MODERATE", lights=true, alarm=false, gsm=false.
        3. Chronic Severe (Current is Biot/Kussmaul, History is consistently severe): risk_level="HIGH", lights=true, alarm=true, gsm=false.
        4. Acute Emergency (Current is Biot/Kussmaul, History is mostly normal/stable): risk_level="CRITICAL", lights=true, alarm=true, gsm=true.
        5. Other patterns: Use clinical judgment to prioritize patient safety.

        Required JSON Format:
        {
            "summary": "Sharp, professional 2-sentence clinical recommendation comparing current session to baseline.",
            "risk_level": "LOW" | "MODERATE" | "HIGH" | "CRITICAL",
            "confidence": 0.95,
            "actuators": {
                "alarm": false,
                "lights": false,
                "gsm": false
            }
        }`;
        
        return await fetchGroq(prompt, true); // expectJson = true
    } else {
        const prompt = `You are a reassuring AI health assistant talking directly to a patient. Their breathing scan just completed with a result of ${aggregatedResult}. Give a comforting 2-sentence advice. Do not use scary medical jargon.`;
        return await fetchGroq(prompt, false);
    }
};

export const generateDeepAnalysis = async (role, historyData, timeframe) => {
    // Tell the AI exactly what time filter the user clicked
    let timeContext = timeframe === 'all' ? 'their entire available history' :
                      timeframe === 'today' ? 'strictly today\'s records' :
                      timeframe === 'week' ? 'the past 7 days' : `specifically ${timeframe}`;

    const prompt = role === 'doctor'
        ? `You are a Pulmonologist AI analyzing a patient's breathing trends for ${timeContext}. Here is the exact data including session aggregates and individual 10-second chunk breakdowns: ${JSON.stringify(historyData)}. Write a 3-sentence professional medical summary. Identify chronic patterns, note specific chunk anomalies if they exist, and assess stability.`
        : `You are an empathetic health AI talking directly to a patient. Analyzing their data for ${timeContext}: ${JSON.stringify(historyData)}. Write a 2-sentence simple summary of this trend. IMPORTANT: Be supportive and calm, but if the data shows a history of severe patterns like Biot, Kussmaul, or Fast Tachypnea, gently advise them to discuss these trends with their healthcare provider. NEVER describe abnormal breathing patterns as a positive sign or congratulate them for it.`;
        
    return await fetchGroq(prompt, false);
};

const fetchGroq = async (prompt, expectJson = false) => {
    try {
        const response = await fetch("https://api.groq.com/openai/v1/chat/completions", {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${GROQ_API_KEY}`,
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                model: "llama-3.3-70b-versatile",
                messages: [{ role: "user", content: prompt }],
                temperature: 0.3, // Lower temperature for more clinical/deterministic output
                max_tokens: 500    // Increased to prevent cut-off sentences
            })
        });
        const data = await response.json();
        const content = data.choices[0].message.content;

        if (expectJson) {
            // Extract pure JSON even if the AI wraps it in markdown blocks
            const jsonMatch = content.match(/\{[\s\S]*\}/);
            return jsonMatch ? jsonMatch[0] : content;
        }

        return content.replace(/["*]/g, ""); 
    } catch (error) {
        console.error("Groq API Error:", error);
        if (expectJson) {
            // Safe fallback if network fails
            return JSON.stringify({
                summary: "Network error. Unable to generate AI triage.",
                risk_level: "UNKNOWN",
                confidence: 0.0,
                actuators: { alarm: false, lights: false, gsm: false }
            });
        }
        return "Unable to generate AI summary at this time. Please check your network connection.";
    }
};